import { CUSTOMER_UNITS_DISPLAY_NAMES_BY_MACHINE } from "constants/customers";
import { UPDATE_DATA_DUMP_PNL_LOSSES_FOR_PROCESSES } from "providers/GlobalStateProvider";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import DataDumpGetterLossCounts from "segments/DataDumpGetter/DataDumpGetter";
import {
  useGetPageFromPath,
  useVisibleProcessIds,
} from "utils/customHooks/genericHooks";
import type { TimeRangeFinite } from "types/types";
import useFlagsWithDefaults from "utils/customHooks/useFlagsWithDefaults";

/** fetch outputLoss from Midgard DataDumpAggregate (DDA)
 *
 * - fetch separately from rest of DDA since some customers must fetch outputLoss from DDA in different ways:
 *
 * - if customer is in CUSTOMER_UNITS_DISPLAY_NAMES_BY_MACHINE,
 * group together machine-ids that use the same unit and fetch separate DDAs for each group e.g. "cups lost, bottles lost"
 *
 * - else, fetch all machine-ids together and call the units something generic like "units lost"
 */
export default function PnlChartLossCountGetter({
  timeRange,
}: {
  timeRange: TimeRangeFinite;
}) {
  const customerMachinesGroupedByUnit = useCustomerMachinesGroupedByUnit();

  // for each unit, fetch a dataDumpAggregate & add it to dataDumpsPnlLossesByUnit

  return (
    <>
      {Object.entries(customerMachinesGroupedByUnit).map(
        ([unit, machemetryIds]) => (
          /** fetch a dataDumpAggregate for one group of machines & add it to dataDumpsPnlLossesByUnit */
          <DataDumpGetterLossCounts
            key={unit}
            timeRange={timeRange}
            dataDumpAggType={UPDATE_DATA_DUMP_PNL_LOSSES_FOR_PROCESSES}
            additionalPayload={{ unit }}
            processIds={machemetryIds}
          />
        )
      )}
    </>
  );
}

export function useShouldFetchPnlChartLossesByUnit() {
  const { isTimelinePage, isTrendsPage, isHubletPage, isGridLayoutPage } =
    useGetPageFromPath();
  const isPnlChartVisible =
    isTimelinePage || isTrendsPage || isHubletPage || isGridLayoutPage;
  const customerMachinesGroupedByUnit = useCustomerMachinesGroupedByUnit();
  const { flightOverrideIsMidgardEnabled } = useFlagsWithDefaults();
  const isFromSnapshotsPage =
    window.location.pathname.includes("fromSnapshots");
  const isPnlHidden = window.location.search.includes("hidePnl=true");
  if (
    isPnlHidden ||
    (isFromSnapshotsPage && flightOverrideIsMidgardEnabled === "disabled")
  ) {
    return false;
  }
  return (
    isPnlChartVisible &&
    customerMachinesGroupedByUnit &&
    Object.keys(customerMachinesGroupedByUnit).length > 0
  );
}

function useCustomerMachinesGroupedByUnit() {
  const ckey = useCustomerKey();
  const customerUnitsByMachine = CUSTOMER_UNITS_DISPLAY_NAMES_BY_MACHINE[ckey];
  const visibleProcessIds = useVisibleProcessIds();

  /** e.g.
   * {
   *   cups: ["pai_d_001", "pai_d_006", "pai_d_007", "pai_d_008"],
   *   bottles: ["pai_d_004", "pai_d_009", "pai_d_010"]
   * }
   */
  const customerMachinesGroupedByUnit = customerUnitsByMachine
    ? Object.entries(customerUnitsByMachine)
        // filter machemetryIds down to visibleProcessIds
        .filter(([machemetryId, unit]) =>
          visibleProcessIds.includes(machemetryId)
        )
        .reduce(
          (acc, [machemetryId, unit]) => {
            acc[unit] = [...(acc[unit] || []), machemetryId];

            return acc;
          },
          {} as { [unit: string]: string[] }
        )
    : {
        // if no units, render a single getter with generic "units" name
        units: visibleProcessIds,
      };
  return customerMachinesGroupedByUnit;
}
